import { Form, Head } from "@inertiajs/react"
import { useTranslation } from "react-i18next"

import InputError from "@/components/input-error"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Spinner } from "@/components/ui/spinner"
import AuthLayout from "@/layouts/auth-layout"
import { identityPasswordResetPath } from "@/routes"

interface ResetPasswordProps {
  sid: string
  email: string
}

export default function ResetPassword({ sid, email }: ResetPasswordProps) {
  const { t } = useTranslation()

  return (
    <AuthLayout
      title={t("auth.password_reset.edit.title")}
      description={t("auth.password_reset.edit.description")}
    >
      <Head title={t("auth.password_reset.edit.page_title")} />
      <Form
        method="put"
        action={identityPasswordResetPath()}
        transform={(data) => ({ ...data, sid, email })}
        resetOnSuccess={["password", "password_confirmation"]}
      >
        {({ processing, errors }) => (
          <div className="grid gap-6">
            <div className="grid gap-2">
              <Label htmlFor="email">
                {t("auth.password_reset.edit.email_label")}
              </Label>
              <Input
                id="email"
                type="email"
                name="email"
                autoComplete="email"
                value={email}
                className="mt-1 block w-full"
                readOnly
              />
              <InputError message={errors.email} className="mt-2" />
            </div>

            <div className="grid gap-2">
              <Label htmlFor="password">
                {t("auth.password_reset.edit.password_label")}
              </Label>
              <Input
                id="password"
                type="password"
                name="password"
                autoComplete="new-password"
                className="mt-1 block w-full"
                autoFocus
                placeholder={t("auth.password_reset.edit.password_placeholder")}
              />
              <InputError message={errors.password} />
            </div>

            <div className="grid gap-2">
              <Label htmlFor="password_confirmation">
                {t("auth.password_reset.edit.password_confirmation_label")}
              </Label>
              <Input
                id="password_confirmation"
                type="password"
                name="password_confirmation"
                autoComplete="new-password"
                className="mt-1 block w-full"
                placeholder={t(
                  "auth.password_reset.edit.password_confirmation_placeholder",
                )}
              />
              <InputError
                message={errors.password_confirmation}
                className="mt-2"
              />
            </div>

            <Button type="submit" className="mt-4 w-full" disabled={processing}>
              {processing && <Spinner />}
              {t("auth.password_reset.edit.submit")}
            </Button>
          </div>
        )}
      </Form>
    </AuthLayout>
  )
}
